import { Clock, Pencil, RotateCcw } from "lucide-react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { agentClient } from "../agentClient";
import { MessagePrimitive, threadFileId, useAuiState, type TextMessagePartProps } from "../aui";
import type { VoiceNoteMeta } from "../aui/convert";
import { useConnectionDown } from "../hooks/useConnectionDown";
import { AttachmentChip } from "./AttachmentChip";
import { MentionText, type MentionFile } from "./MentionChip";
import { LIMIT_REACHED } from "./models";
import { useThreadId } from "./useThreadId";
import { VoiceNote } from "./VoiceNote";

const MessageChip = () => <AttachmentChip />;

/** The message's text, its mentions of the message's own files as chips. */
function UserText({ text }: TextMessagePartProps) {
  const threadId = useThreadId();
  const attachments = useAuiState((state) => state.message.attachments);
  const files = useMemo(
    () =>
      (attachments ?? []).flatMap((attachment): MentionFile[] => {
        const id = threadFileId(attachment.id);
        return id == null
          ? []
          : [{ id, name: attachment.name, contentType: attachment.contentType ?? "" }];
      }),
    [attachments]
  );
  return <MentionText text={text} files={files} threadId={threadId} />;
}

/** What a stored or outgoing user message carries beside its parts (see aui/convert). */
interface UserMeta {
  pending?: boolean;
  state?: "sending" | "sent" | "failed";
  error?: string | null;
  errorCode?: string | null;
  voice?: VoiceNoteMeta[];
}

/** What a failed send says: the server's reason, short; the daily limit without its mechanics. */
function failure(meta: UserMeta): string {
  if (meta.errorCode === "budget_exceeded") return LIMIT_REACHED;
  return meta.error || "Not sent";
}

/** Back to this chat's composer: the failed row goes away under the button just pressed. */
export function focusComposer(from: HTMLElement) {
  from.closest("[data-aui-thread-root]")?.querySelector("textarea")?.focus();
}

function SendFailed({ cid, error }: { cid: string; error: string }) {
  return (
    // On touch the two buttons sit apart, so their 44px targets don't overlap.
    <div
      role="alert"
      className="flex items-center gap-0.5 text-xs text-destructive [@media(pointer:coarse)]:[&_[data-compact]]:mx-2.5"
    >
      <span className="mr-1">{error}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 max-sm:h-10 max-sm:w-10"
        aria-label="Send message again"
        data-compact=""
        onClick={(event) => {
          focusComposer(event.currentTarget);
          agentClient.retrySend(cid);
        }}
      >
        <RotateCcw className="!size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 max-sm:h-10 max-sm:w-10"
        aria-label="Edit message"
        data-compact=""
        onClick={(event) => {
          focusComposer(event.currentTarget);
          agentClient.takeBack(cid);
        }}
      >
        <Pencil className="!size-3.5" />
      </Button>
    </div>
  );
}

/**
 * The user's message: files, then the text in a bubble, then (sent on a messaging channel) its voice
 * notes in the bubble. One sent while an answer runs is muted until its turn comes, as is one
 * waiting for the connection (it says so); one that failed to send (a tab the server refused fails
 * its sends) says why, with Send again and Edit.
 */
export function UserMessage() {
  const id = useAuiState((state) => state.message.id);
  const meta = useAuiState((state) => state.message.metadata.custom) as UserMeta;
  const down = useConnectionDown();
  const waiting = meta.state === "sending" && down === "reconnecting";
  const hasText = useAuiState((state) =>
    state.message.content.some((part) => part.type === "text" && part.text.trim() !== "")
  );
  const voice = meta.voice ?? [];
  return (
    <MessagePrimitive.Root
      role="article"
      aria-label="You"
      className={cn(
        "flex flex-col items-end gap-1.5 transition-opacity duration-fade ease-linear",
        (meta.pending || waiting) && "opacity-60"
      )}
    >
      {/* Muted while it waits for the answer above it; said in words too. */}
      {meta.pending && <span className="sr-only">Queued</span>}
      <div className="flex max-w-[85%] flex-wrap justify-end gap-1.5 empty:hidden">
        <MessagePrimitive.Attachments components={{ Attachment: MessageChip }} />
      </div>
      {(hasText || voice.length > 0) && (
        <div
          className={cn(
            "max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-[4px] bg-accent px-3.5 py-2 text-sm text-foreground",
            voice.length > 0 && "flex flex-col gap-3"
          )}
        >
          {/* Beside voice notes, the text keeps its own line box. */}
          {hasText &&
            (voice.length > 0 ? (
              <div>
                <MessagePrimitive.Parts components={{ Text: UserText }} />
              </div>
            ) : (
              <MessagePrimitive.Parts components={{ Text: UserText }} />
            ))}
          {voice.map((note) => (
            <VoiceNote key={note.id} note={note} />
          ))}
        </div>
      )}
      {/* Read with the message, not announced: the composer's notice is the one announcement. */}
      {waiting && (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" aria-hidden />
          Waiting for connection
        </p>
      )}
      {meta.state === "failed" && id.startsWith("out:") && (
        <SendFailed cid={id.slice("out:".length)} error={failure(meta)} />
      )}
    </MessagePrimitive.Root>
  );
}
